import { useEffect, useRef } from 'react'

import { parsePhoneNumberFromString } from 'libphonenumber-js'

import { useCreateAccount } from '@/api/v1/auth/create-account/use-create-account'
import type { AccountProfileData } from '@/components/common/account-profile-form'
import { AccountProfileForm } from '@/components/common/account-profile-form'
import { logger } from '@/lib/logger'
import { useLanguageStore } from '@/stores/language'

import type { CreateAccountSource } from './create-account-wizard.tracking'
import {
  trackCreateAccountStepCompleted,
  trackCreateAccountStepViewed,
  trackCreateAccountWizardAbandoned,
  trackCreateAccountWizardCompleted,
  trackCreateAccountWizardOpened,
} from './create-account-wizard.tracking'

interface CreateAccountWizardProps {
  source: CreateAccountSource
  firstAccount: boolean
  onComplete: (userAccountId: string) => void
  onClose?: () => void
}

export function CreateAccountWizard({
  source,
  firstAccount,
  onComplete,
  onClose,
}: CreateAccountWizardProps) {
  const createAccount = useCreateAccount()
  const locale = useLanguageStore((s) => s.locale)
  const openedAtRef = useRef(Date.now())
  const stepStartedAtRef = useRef(Date.now())
  const hasCompletedRef = useRef(false)

  useEffect(() => {
    trackCreateAccountWizardOpened({ source, firstAccount })
    trackCreateAccountStepViewed({
      source,
      firstAccount,
      step: 'profile',
      stepNumber: 1,
      accountType: null,
    })
  }, [source, firstAccount])

  useEffect(() => {
    return () => {
      if (!hasCompletedRef.current) {
        trackCreateAccountWizardAbandoned({
          source,
          firstAccount,
          lastStep: 'profile',
          accountType: null,
          durationMs: Date.now() - openedAtRef.current,
        })
      }
    }
  }, [])

  const handleProfileComplete = async (data: AccountProfileData) => {
    trackCreateAccountStepCompleted({
      source,
      firstAccount,
      step: 'profile',
      stepNumber: 1,
      accountType: data.type,
      durationMs: Date.now() - stepStartedAtRef.current,
    })

    const parsed = parsePhoneNumberFromString(data.phone)
    if (!parsed) {
      logger.error('Failed to parse phone number during account creation')
      return
    }

    try {
      const language = locale.charAt(0).toUpperCase() + locale.slice(1)

      const result = await createAccount.mutateAsync({
        countryCode: data.countryCode,
        type: data.type === 'Company' ? 'Company' : 'Personal',
        accountName: data.accountName,
        phoneIdd: Number(parsed.countryCallingCode),
        phoneNumber: String(parsed.nationalNumber),
        language,
      })

      trackCreateAccountWizardCompleted({
        source,
        firstAccount,
        accountType: data.type,
        countryCode: data.countryCode,
        totalDurationMs: Date.now() - openedAtRef.current,
      })

      hasCompletedRef.current = true
      onComplete(result.userAccountId)
      onClose?.()
    } catch (error) {
      logger.error(
        'Failed to create account',
        error instanceof Error ? error : undefined,
      )
    }
  }

  return (
    <AccountProfileForm
      onSubmit={handleProfileComplete}
      isLoading={createAccount.isPending}
    />
  )
}
